import { Component, computed, input, output } from '@angular/core';
import { LucideDynamicIcon } from '@lucide/angular';
import { ButtonComponent } from '../../atoms/button/button.component';
import { TextDirective } from '../../typography/text.directive';
import { CallAttachmentAction } from './models/call-actions';
import { CallAttachmentState } from './models/call-state';
import {
  callAttachmentAction,
  callAttachmentDescription,
  callAttachmentIcon,
  callAttachmentTitle,
} from './models/call-utils';

@Component({
  selector: 'joii-call-custom-attachment',
  template: `
    <div class="attachment-content">
      <div class="attachment-title">
        <div class="attachment-icon"><svg [lucideIcon]="icon()" [size]="20" /></div>
        <span joii-text [size]="6" weight="semibold">{{ title() }}</span>
      </div>
      <p joii-text [size]="5">{{ description() }}</p>
    </div>
    @if (actionButton()) {
      <div class="attachment-actions">
        @switch (actionButton()) {
          @case ('join') {
            <button class="join-button" joii-button variant="success-filled" size="lg" (click)="action.emit('join')">
              Join video call
            </button>
          }
        }
      </div>
    }
  `,
  host: {
    '[attr.data-state]': 'state()',
  },
  styles: `
    :host {
      --attachment-width: 361px;
      display: flex;
      flex-direction: column;
      gap: var(--Vetai-Spacing-3, 12px);
      background: var(--Vetai-Backgrounds--Base);
      border: 0.5px solid var(--Vetai-Backgrounds--Highlight);
      padding: var(--Vetai-Spacing-2, 8px) var(--Vetai-Spacing-2, 8px) var(--Vetai-Spacing-2, 8px)
        var(--Vetai-Spacing-2, 8px);
      border-radius: var(--Vetai-Radius-4);
      width: min(var(--attachment-width), 100%);
    }
    .attachment-content {
      display: flex;
      flex-direction: column;
      gap: var(--Vetai-Spacing-3, 12px);
      background: var(--Vetai-Backgrounds--Tint_1);
      border-radius: var(--Vetai-Radius-2-5);
      padding: var(--Vetai-Spacing-2, 8px) var(--Vetai-Spacing-2, 8px) var(--Vetai-Spacing-4, 16px)
        var(--Vetai-Spacing-2, 8);
    }
    .attachment-title {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-2, 8px);
    }
    .attachment-icon {
      width: 40px;
      height: 40px;
      padding: 10px;
      border-radius: var(--Vetai-Radius-100, 100px);
      aspect-ratio: 1/1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--Vetai-Negative_package--Default);
    }

    [data-theme='dark'] :host .attachment-icon {
      color: var(--Vetai-Positive_package--Default);
    }

    :host([data-state='Ready']) .attachment-icon,
    :host([data-state='InProgress']) .attachment-icon {
      background: var(--Vetai-Status_packages--Success--Transparent_50);
    }

    :host([data-state='Complete']) .attachment-icon {
      background: var(--Vetai-Secondary--Default);
      color: var(--Vetai-Positive_package--Default);
    }
    :host([data-state='Disrupted']) .attachment-icon,
    :host([data-state='Missed']) .attachment-icon {
      background: var(--Vetai-Status_packages--Error--Transparent_50);
    }

    .join-button {
      --joii-button-text-color: var(--Vetai-Absolute_packages--White--Default);
      width: 100%;
    }
    [data-theme='dark'] :host .join-button {
      --joii-button-text-color: var(--Vetai-Absolute_packages--Black--Default);
    }
    .join-button,
    .join-button * {
      color: var(--joii-button-text-color) !important;
    }
  `,
  imports: [LucideDynamicIcon, TextDirective, ButtonComponent],
})
export class CallCustomAttachmentComponent {
  state = input.required<CallAttachmentState>();
  joined = input<boolean>(false);
  staffName = input<string>();
  callDuration = input<number>();
  action = output<CallAttachmentAction>();
  title = computed(() => callAttachmentTitle(this.state()!));
  description = computed(() => callAttachmentDescription(this.state(), this.staffName(), this.callDuration()));
  icon = computed(() => callAttachmentIcon(this.state()));
  actionButton = computed(() => callAttachmentAction(this.state(), this.joined()));
}
